import { Button } from "components/DesignSystem/Button/Button";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { Field, Form, Formik } from "formik";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { completeBookkeepingValidation } from "formValidations/completeBookkeepingValidation";
import { Task } from "types/Models/task";
import { NumericInput } from "components/NumericInput/NumericInput";
import { PriceInput } from "components/PriceInput/PriceInput";
import { Price } from "components/Price";
import { ACCOUNTING_METHODS } from "constants/bookkeeping";
import { useModal } from "hooks/useModal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import dayjs from "dayjs";
import { YYYY_MM_DD } from "constants/date";
import { useState } from "react";
import { FormikListener } from "pages/Books/Invoicing/AddInvoice";
import { useGetBookkeepingQuoteQuery } from "store/apis/bookkeepingQuote";

export type BookkeepingFormValues = {
  accounting_method: string;
  from_date: string | null;
  to_date: string | null;
  no_of_transactions: number;
  cash_expense: number;
  intra_group_transfers: number;
  intra_company_transfers: number;
  monthly_billable_expenses_volume: number;
};

type BookkeepingModalProps = {
  show: boolean;
  onClose: () => void;
  onSubmit: (values: BookkeepingFormValues) => void;
  isSubmitting: boolean;
  task: Task;
};

export const BookkeepingModal = ({
  show,
  onClose,
  onSubmit,
  isSubmitting,
  task,
}: BookkeepingModalProps) => {
  const [startDate, setStartDate] = useState<string | null>(
    task?.from_date ? dayjs(task?.from_date).format(YYYY_MM_DD) : null
  );
  const [endDate, setEndDate] = useState<string | null>(
    task?.to_date ? dayjs(task?.to_date).format(YYYY_MM_DD) : null
  );
  const [accountingMethod, setAccountingMethod] = useState<string>(
    task?.accounting_method || ACCOUNTING_METHODS.ACCRUAL
  );

  const options = [
    { value: ACCOUNTING_METHODS.ACCRUAL, label: "Accrual" },
    { value: ACCOUNTING_METHODS.CASH, label: "Cash" },
  ];

  const confirmationModal = useModal();

  const { alertToast } = useToast();

  const onFormChange = (values: Record<string, any>) => {
    setStartDate(values.from_date);
    setEndDate(values.to_date);
    setAccountingMethod(values.accounting_method);
  };

  const { data: quote } = useGetBookkeepingQuoteQuery({
    entity_id: task.entity.uuid,
    from_date: startDate || "",
    to_date: endDate || "",
  });

  const initialValues: BookkeepingFormValues = {
    accounting_method: accountingMethod,
    from_date: startDate,
    to_date: endDate,
    no_of_transactions: quote?.no_of_transactions || 0,
    cash_expense: quote?.cash_expense || 0,
    intra_group_transfers: quote?.intra_group_transfers || 0,
    intra_company_transfers: quote?.intra_company_transfers || 0,
    monthly_billable_expenses_volume:
      quote?.monthly_billable_expenses_volume || 0,
  };

  return (
    <Modal.Root open={show} onOpenChange={onClose}>
      <Modal.Content useCustomOverlay asChild>
        <FormikForm
          validateOnMount
          validationSchema={completeBookkeepingValidation}
          // @ts-ignore
          onSubmit={onSubmit}
          initialValues={initialValues}
          enableReinitialize
        >
          {({ submitForm, validateForm, setFieldValue, isValid, values }) => {
            return (
              <>
                <FormikListener values={values} callback={onFormChange} />
                <Modal.Header>
                  <Modal.Title>Mark filing as done</Modal.Title>
                  <Modal.Close type="button" onClick={onClose} />
                </Modal.Header>
                <Modal.Body>
                  <div>
                    <div className="t-text-body-sm t-text-text-30">
                      Fill in the details to complete the task.
                    </div>
                    <div className="t-flex-1 t-my-4">
                      <Banner
                        description="The numbers above are fetched from Inkle Books.  Update manually if you are using other accounting software."
                        type="neutral"
                      />
                    </div>
                    <div className="t-flex t-gap-4 t-flex-col">
                      <Combobox
                        withForm
                        block
                        value={options.find(
                          (option) => option.value === values.accounting_method
                        )}
                        required
                        name="accounting_method"
                        label="Accounting method*"
                        options={options}
                      />
                      <div className="t-grid t-grid-cols-2 t-gap-4">
                        <Field name="from_date">
                          {({ field }: { field: any }) => {
                            return (
                              <DateInput
                                {...field}
                                required
                                showMonthYearPicker
                                portalId={field.name}
                                label="Start from"
                              />
                            );
                          }}
                        </Field>
                        <Field name="to_date">
                          {({ field }: { field: any }) => (
                            <DateInput
                              {...field}
                              onDateChange={(date: Date) => {
                                setFieldValue(
                                  field.name,
                                  dayjs(date).endOf("M").format(YYYY_MM_DD)
                                );
                              }}
                              required
                              showMonthYearPicker
                              label="To"
                              portalId={field.name}
                            />
                          )}
                        </Field>
                      </div>
                      <NumericInput
                        label="No.of transactions"
                        storeNumeric
                        required
                        fieldProps={{
                          name: "no_of_transactions",
                        }}
                      />
                      {values.accounting_method ===
                        ACCOUNTING_METHODS.ACCRUAL && (
                        <>
                          <PriceInput
                            required
                            name="cash_expense"
                            label="Cash Expenses"
                          />

                          <div className="t-grid t-grid-cols-2 t-gap-4">
                            <PriceInput
                              required
                              name="intra_group_transfers"
                              type="number"
                              label="Intra-group transfers"
                            />
                            <PriceInput
                              required
                              name="intra_company_transfers"
                              type="number"
                              label="Intra-company transfers"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button type="button">Cancel</Button>
                  </Modal.RawClose>
                  <Modal.Root
                    open={confirmationModal.isOpen}
                    onOpenChange={confirmationModal.close}
                  >
                    <Modal.Trigger
                      asChild
                      onClick={async () => {
                        const errors = await validateForm();
                        if (Object.entries(errors).length > 0) {
                          return alertToast({
                            message: "Please fill all the required fields.",
                          });
                        }
                        confirmationModal.open();
                      }}
                    >
                      <Button customType="primary" type="button">
                        Next
                      </Button>
                    </Modal.Trigger>
                    <Modal.Content>
                      <Modal.Header>
                        <Modal.Title>Confirm action</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <p className="t-m-0 t-text-body-lg">
                          You're about to close the filing for {task.title}.
                          This is a final action, once approved, no further
                          edits can be made to the filing.
                          <br />
                          <br />
                          Are you sure?
                        </p>
                      </Modal.Body>
                      <Modal.FooterButtonGroup>
                        <Modal.RawClose asChild>
                          <Button type="button" disabled={isSubmitting}>
                            Go back
                          </Button>
                        </Modal.RawClose>
                        <Button
                          customType="primary"
                          type="submit"
                          onClick={submitForm}
                          disabled={!isValid || isSubmitting}
                          isLoading={isSubmitting}
                        >
                          Confirm
                        </Button>
                      </Modal.FooterButtonGroup>
                    </Modal.Content>
                  </Modal.Root>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
